import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import TopButton from "../../components/topButton/TopButton";
import ProjectsData from "../../shared/opensource/projects.json";
import PinnedProjectsData from "../../shared/opensource/pinned_projects.json";
import PullRequestData from "../../shared/opensource/pull_requests.json";
import IssuesData from "../../shared/opensource/issues.json";
import OrganizationsData from "../../shared/opensource/organizations.json";
import CommitActivityData from "../../shared/opensource/commit_activity.json";
import { greeting } from "../../portfolio.js";
import "./Opensource.css";

const languageColors = {
  Python: "#6ca8ff",
  JavaScript: "#f6ce54",
  TypeScript: "#6ca8ff",
  HTML: "#ff8a65",
  CSS: "#b69cff",
  Dockerfile: "#63d8ff",
  Shell: "#82e8a8",
  Code: "#7cf7ff",
};

const getArray = (value) => (Array.isArray(value) ? value : []);

const repositories = getArray(ProjectsData && ProjectsData.data);
const pinnedRepositories = getArray(
  PinnedProjectsData && PinnedProjectsData.data
);
const pullRequests = getArray(PullRequestData && PullRequestData.data);
const issues = getArray(IssuesData && IssuesData.data);
const organizations = getArray(OrganizationsData && OrganizationsData.data);
const commitCalendar = getArray(
  CommitActivityData && CommitActivityData.calendar
);
const pinnedIds = new Set(pinnedRepositories.map((repo) => repo.id));

function compactNumber(value) {
  const number = Number(value) || 0;

  if (number >= 1000) {
    const digits = number >= 10000 ? 0 : 1;
    return `${(number / 1000).toFixed(digits)}k`;
  }

  return number.toLocaleString();
}

function formatDate(value, options) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Snapshot";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    options || { month: "short", day: "numeric", year: "numeric" }
  ).format(date);
}

function timestamp(value) {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function hasNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function getPrimaryLanguage(repo) {
  if (repo && repo.primaryLanguage) return repo.primaryLanguage;
  const languages = getArray(repo && repo.languages);
  return languages[0] && languages[0].name ? languages[0].name : "Code";
}

function getLanguages(repo) {
  const languages = getArray(repo && repo.languages)
    .map((language) => language && language.name)
    .filter(Boolean);

  if (languages.length > 0) return languages.slice(0, 3);
  return [getPrimaryLanguage(repo)];
}

function cleanedDescription(description) {
  if (!description) return "A focused build from Sethuram's open-source lab.";
  return description.replace(/\s+/g, " ").trim();
}

function Glyph({ name, size = 18 }) {
  const props = {
    "aria-hidden": "true",
    fill: "none",
    height: size,
    viewBox: "0 0 24 24",
    width: size,
    xmlns: "http://www.w3.org/2000/svg",
  };

  if (name === "github") {
    return (
      <svg {...props} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.477 2 2 6.59 2 12.253c0 4.53 2.865 8.373 6.839 9.73.5.095.682-.223.682-.494 0-.243-.009-.887-.014-1.74-2.782.62-3.369-1.378-3.369-1.378-.455-1.185-1.11-1.5-1.11-1.5-.908-.638.069-.625.069-.625 1.004.073 1.532 1.057 1.532 1.057.892 1.57 2.341 1.116 2.91.853.091-.666.349-1.116.635-1.373-2.221-.261-4.556-1.14-4.556-5.073 0-1.121.39-2.036 1.03-2.754-.104-.26-.447-1.31.098-2.73 0 0 .84-.276 2.75 1.052A9.372 9.372 0 0 1 12 6.93c.85.004 1.706.118 2.505.347 1.91-1.328 2.748-1.052 2.748-1.052.547 1.42.204 2.47.1 2.73.64.718 1.028 1.633 1.028 2.754 0 3.943-2.339 4.81-4.568 5.067.36.32.68.95.68 1.915 0 1.382-.012 2.497-.012 2.836 0 .274.18.594.688.493C19.14 20.623 22 16.78 22 12.253 22 6.59 17.523 2 12 2Z" />
      </svg>
    );
  }

  if (name === "star") {
    return (
      <svg {...props}>
        <path
          d="m12 3 2.78 5.63 6.22.9-4.5 4.39 1.06 6.2L12 17.2l-5.56 2.92 1.06-6.2L3 9.53l6.22-.9L12 3Z"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.7"
        />
      </svg>
    );
  }

  if (name === "fork") {
    return (
      <svg {...props}>
        <circle cx="7" cy="5" r="2.4" stroke="currentColor" strokeWidth="1.7" />
        <circle
          cx="17"
          cy="19"
          r="2.4"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M7 7.4v4.1c0 2.1 1.7 3.8 3.8 3.8H14a3 3 0 0 1 3 3"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.7"
        />
      </svg>
    );
  }

  if (name === "merge") {
    return (
      <svg {...props}>
        <circle cx="7" cy="5" r="2.3" stroke="currentColor" strokeWidth="1.7" />
        <circle
          cx="7"
          cy="19"
          r="2.3"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle
          cx="17"
          cy="12"
          r="2.3"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M7 7.3v4.1c0 2.6 2.1 4.7 4.7 4.7H14.5M9.2 16.7 14.7 12H17"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.7"
        />
      </svg>
    );
  }

  if (name === "pulse") {
    return (
      <svg {...props}>
        <path
          d="M3 12h3l2.1-5.2L12 18l2.5-6H21"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  if (name === "code") {
    return (
      <svg {...props}>
        <path
          d="m8.5 8-4 4 4 4M15.5 8l4 4-4 4M13.5 5l-3 14"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  if (name === "arrow") {
    return (
      <svg {...props}>
        <path
          d="M5 12h14M13 6l6 6-6 6"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  if (name === "issue") {
    return (
      <svg {...props}>
        <circle
          cx="12"
          cy="12"
          r="8.4"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M12 8v4.5M12 16h.01"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  return (
    <svg {...props}>
      <path
        d="M5 12h14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function MetricCard({ icon, label, value, note }) {
  return (
    <article className="os-metric-card">
      <span className="os-metric-icon">
        <Glyph name={icon} size={17} />
      </span>
      <p>{label}</p>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function RepositoryCard({ repo, index }) {
  const language = getPrimaryLanguage(repo);
  const languages = getLanguages(repo);
  const isPinned = pinnedIds.has(repo.id);
  const color = languageColors[language] || languageColors.Code;

  return (
    <article
      className="os-repository-card"
      style={{ "--repo-accent": color, "--card-index": index }}
    >
      <div className="os-repository-card-topline">
        <span className="os-repo-signal">
          <span></span>
          {isPinned ? "Pinned build" : "Open source"}
        </span>
        <span className="os-repo-language">
          <i></i>
          {language}
        </span>
      </div>

      <a
        className="os-repo-title"
        href={repo.url || greeting.githubProfile}
        target="_blank"
        rel="noopener noreferrer"
      >
        {repo.name || "Untitled repository"}
        <Glyph name="arrow" size={16} />
      </a>

      <p className="os-repo-description">
        {cleanedDescription(repo.description)}
      </p>

      <div
        className="os-repo-tags"
        aria-label={`${repo.name || "Repository"} technologies`}
      >
        {languages.map((item) => (
          <span key={`${repo.id}-${item}`}>{item}</span>
        ))}
      </div>

      <div className="os-repository-footer">
        <div className="os-repo-stats" aria-label="Repository popularity">
          <span>
            <Glyph name="star" size={14} /> {compactNumber(repo.stargazerCount)}
          </span>
          <span>
            <Glyph name="fork" size={14} /> {compactNumber(repo.forkCount)}
          </span>
        </div>
        <time dateTime={repo.updatedAt}>
          Updated{" "}
          {formatDate(repo.updatedAt, { month: "short", year: "numeric" })}
        </time>
      </div>
    </article>
  );
}

function PullRequestRow({ pullRequest, index }) {
  const state = (pullRequest.state || "OPEN").toLowerCase();
  const repository = pullRequest.baseRepository || {};
  const owner = repository.owner || {};
  const additions = Number(pullRequest.additions) || 0;
  const deletions = Number(pullRequest.deletions) || 0;

  return (
    <a
      className="os-pr-row"
      href={pullRequest.url || greeting.githubProfile}
      target="_blank"
      rel="noopener noreferrer"
      style={{ "--row-index": index }}
    >
      <span className={`os-pr-state os-pr-state-${state}`}>
        <Glyph name={state === "merged" ? "merge" : "pulse"} size={16} />
      </span>
      <span className="os-pr-copy">
        <strong>{pullRequest.title || "Untitled pull request"}</strong>
        <small>
          {owner.login || "Sethuram2003"}/{repository.name || "repository"}{" "}
          <b>#{pullRequest.number || "—"}</b>
        </small>
      </span>
      <span className="os-pr-date">
        {formatDate(pullRequest.createdAt, { month: "short", day: "numeric" })}
      </span>
      <span className="os-pr-delta">
        <b>+{compactNumber(additions)}</b>
        <em>−{compactNumber(deletions)}</em>
      </span>
      <span className={`os-status-pill os-status-${state}`}>{state}</span>
      <Glyph name="arrow" size={16} />
    </a>
  );
}

function VelocityChart({ data }) {
  if (data.length === 0) {
    return (
      <div className="os-velocity-empty">
        <Glyph name="pulse" size={22} />
        <span>Activity bars will appear after the next GitHub data sync.</span>
      </div>
    );
  }

  const largest = Math.max(...data.map((item) => item.count), 1);

  return (
    <div
      className="os-velocity-chart"
      role="img"
      aria-label="Merged pull request velocity by active month"
    >
      {data.map((item) => {
        const height = Math.max(12, Math.round((item.count / largest) * 100));
        return (
          <div
            className="os-velocity-column"
            key={item.key}
            title={`${item.label}: ${item.count} pull request${
              item.count === 1 ? "" : "s"
            }`}
          >
            <span>{item.count}</span>
            <i style={{ height: `${height}%` }}></i>
            <small>{item.label}</small>
          </div>
        );
      })}
    </div>
  );
}

function CommitTelemetry({
  calendar,
  totalCommits,
  totalContributions,
  generatedAt,
}) {
  const hasCalendar = calendar.length > 0;
  const sortedCalendar = [...calendar].sort(
    (first, second) => timestamp(first.date) - timestamp(second.date)
  );

  return (
    <article className="os-commit-panel">
      <div className="os-commit-panel-head">
        <div>
          <span>COMMIT TELEMETRY</span>
          <strong>
            {hasCalendar
              ? "A year of contribution signal"
              : "Commit telemetry refresh pending"}
          </strong>
        </div>
        <div className={`os-commit-total ${hasCalendar ? "is-ready" : ""}`}>
          <b>{hasNumber(totalCommits) ? compactNumber(totalCommits) : "—"}</b>
          <small>
            {hasNumber(totalCommits)
              ? "commits / 12 months"
              : "no count invented"}
          </small>
        </div>
      </div>

      {hasCalendar ? (
        <>
          <div
            className="os-commit-calendar"
            role="img"
            aria-label={`${compactNumber(
              totalContributions
            )} GitHub contributions across the last 12 months`}
          >
            {sortedCalendar.map((day, index) => {
              const count = Number(day.count) || 0;
              return (
                <span
                  key={`${day.date || "day"}-${index}`}
                  className={count > 0 ? "is-active" : ""}
                  style={
                    count > 0 && day.color
                      ? { "--activity-color": day.color }
                      : undefined
                  }
                  title={`${formatDate(day.date)}: ${count} contribution${
                    count === 1 ? "" : "s"
                  }`}
                ></span>
              );
            })}
          </div>
          <div className="os-commit-panel-foot">
            <span>{compactNumber(totalContributions)} total contributions</span>
            <span>
              Less <i></i>
              <i></i>
              <i></i>
              <i></i>
              <i></i> More
            </span>
            <time dateTime={generatedAt || undefined}>
              {generatedAt
                ? `Exported ${formatDate(generatedAt)}`
                : "GitHub export"}
            </time>
          </div>
        </>
      ) : (
        <div className="os-commit-pending">
          <span className="os-commit-pending-mark">
            <Glyph name="pulse" size={19} />
          </span>
          <div>
            <strong>Ready for the next GitHub export.</strong>
            <p>
              Commit history is deliberately empty in this local fallback. Run
              the repository’s GitHub data refresh to light up this 12-month
              calendar with verified counts.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

function Opensource(props) {
  const { theme = {}, isDark } = props;
  const [languageFilter, setLanguageFilter] = useState("All");
  const [sortMode, setSortMode] = useState("impact");
  const [showAllRepos, setShowAllRepos] = useState(false);
  const [showAllPullRequests, setShowAllPullRequests] = useState(false);

  const githubProfile = greeting.githubProfile || "https://github.com";
  const totalStars = repositories.reduce(
    (total, repo) => total + (Number(repo.stargazerCount) || 0),
    0
  );
  const totalForks = repositories.reduce(
    (total, repo) => total + (Number(repo.forkCount) || 0),
    0
  );
  const totalAdditions = pullRequests.reduce(
    (total, pr) => total + (Number(pr.additions) || 0),
    0
  );
  const totalDeletions = pullRequests.reduce(
    (total, pr) => total + (Number(pr.deletions) || 0),
    0
  );
  const mergedPullRequests =
    typeof PullRequestData.merged === "number"
      ? PullRequestData.merged
      : pullRequests.filter((pr) => pr.state === "MERGED").length;
  const totalPullRequests =
    typeof PullRequestData.totalCount === "number"
      ? PullRequestData.totalCount
      : pullRequests.length;
  const mergeRate =
    totalPullRequests > 0
      ? Math.round((mergedPullRequests / totalPullRequests) * 100)
      : 0;
  const trackedIssues =
    typeof IssuesData.totalCount === "number"
      ? IssuesData.totalCount
      : issues.length;
  const commitCount = CommitActivityData
    ? CommitActivityData.totalCommits
    : null;
  const contributionCount = CommitActivityData
    ? CommitActivityData.totalContributions
    : null;
  const hasCommitTelemetry =
    hasNumber(commitCount) || commitCalendar.length > 0;

  const languages = useMemo(() => {
    const counts = repositories.reduce((collection, repo) => {
      const language = getPrimaryLanguage(repo);
      collection[language] = (collection[language] || 0) + 1;
      return collection;
    }, {});

    return Object.keys(counts)
      .sort((first, second) => counts[second] - counts[first])
      .slice(0, 5);
  }, []);

  const visibleRepositories = useMemo(() => {
    const filtered = repositories.filter((repo) => {
      return (
        languageFilter === "All" || getPrimaryLanguage(repo) === languageFilter
      );
    });

    return filtered.sort((first, second) => {
      if (sortMode === "recent")
        return timestamp(second.updatedAt) - timestamp(first.updatedAt);
      if (sortMode === "name")
        return (first.name || "").localeCompare(second.name || "");

      const firstImpact =
        (Number(first.stargazerCount) || 0) * 4 +
        (Number(first.forkCount) || 0);
      const secondImpact =
        (Number(second.stargazerCount) || 0) * 4 +
        (Number(second.forkCount) || 0);
      return (
        secondImpact - firstImpact ||
        timestamp(second.updatedAt) - timestamp(first.updatedAt)
      );
    });
  }, [languageFilter, sortMode]);

  const sortedPullRequests = useMemo(() => {
    return [...pullRequests].sort(
      (first, second) =>
        timestamp(second.createdAt) - timestamp(first.createdAt)
    );
  }, []);

  const velocity = useMemo(() => {
    const buckets = pullRequests.reduce((collection, pullRequest) => {
      const date = new Date(pullRequest.createdAt);
      if (Number.isNaN(date.getTime())) return collection;

      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      if (!collection[key]) {
        collection[key] = {
          key,
          count: 0,
          label: new Intl.DateTimeFormat("en-US", { month: "short" }).format(
            date
          ),
        };
      }
      collection[key].count += 1;
      return collection;
    }, {});

    return Object.keys(buckets)
      .sort()
      .slice(-7)
      .map((key) => buckets[key]);
  }, []);

  const latestSignal = [
    ...repositories.map((repo) => repo.updatedAt),
    ...pullRequests.map((pr) => pr.createdAt),
  ]
    .filter(Boolean)
    .sort((first, second) => timestamp(second) - timestamp(first))[0];
  const displayedRepositories = showAllRepos
    ? visibleRepositories
    : visibleRepositories.slice(0, 6);
  const displayedPullRequests = showAllPullRequests
    ? sortedPullRequests
    : sortedPullRequests.slice(0, 6);
  const cssVariables = {
    "--os-ink": theme.text || "#0A1927",
    "--os-muted": theme.secondaryText || "#4F6577",
    "--os-accent": theme.imageHighlight || "#057E91",
    "--os-surface": isDark ? "#0c1320" : "#f8fcfd",
    "--os-panel": isDark ? "rgba(17, 26, 43, .86)" : "rgba(255, 255, 255, .82)",
    "--os-panel-strong": isDark ? "#111a2b" : "#ffffff",
    "--os-border": isDark ? "rgba(152, 199, 228, .16)" : "rgba(8, 46, 73, .13)",
    "--os-grid": isDark
      ? "rgba(124, 247, 255, .075)"
      : "rgba(5, 126, 145, .08)",
    "--os-shadow": isDark
      ? "0 24px 80px rgba(0, 0, 0, .32)"
      : "0 24px 80px rgba(10, 25, 39, .09)",
  };

  const radarValue = hasNumber(commitCount)
    ? compactNumber(commitCount)
    : compactNumber(totalPullRequests);
  const radarLabel = hasNumber(commitCount) ? "commits / 12m" : "PR signals";

  return (
    <div className="opensource-main">
      <Header theme={theme} onToggle={props.onToggle} isDark={isDark} />

      <main className="observatory-page" style={cssVariables}>
        <section className="os-hero" aria-labelledby="opensource-heading">
          <div className="os-hero-grid" aria-hidden="true"></div>
          <div
            className="os-hero-glow os-hero-glow-one"
            aria-hidden="true"
          ></div>
          <div
            className="os-hero-glow os-hero-glow-two"
            aria-hidden="true"
          ></div>

          <div className="os-hero-copy">
            <div className="os-eyebrow">
              <span className="os-status-light"></span>
              OPEN SOURCE / OBSERVATORY
              <span className="os-eyebrow-rule"></span>
              {hasCommitTelemetry
                ? "COMMIT TELEMETRY ONLINE"
                : "COMMIT TELEMETRY REFRESH PENDING"}
            </div>

            <h1 id="opensource-heading">
              Code that <em>keeps moving.</em>
            </h1>
            <p>
              A navigable field log of AI agents, local-first infrastructure,
              and the pull requests that move each build forward.
            </p>

            <div className="os-hero-actions">
              <Link
                className="os-button os-button-primary"
                to="/contact"
                aria-label="Discuss an opportunity with Sethuram"
              >
                Discuss an opportunity <Glyph name="arrow" size={16} />
              </Link>
              <a
                className="os-button os-button-quiet"
                href={githubProfile}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Glyph name="github" size={18} /> View GitHub profile{" "}
                <Glyph name="arrow" size={16} />
              </a>
            </div>

            <p className="os-snapshot-note">
              <Glyph name="pulse" size={15} />
              {hasCommitTelemetry && CommitActivityData.generatedAt
                ? `Commit telemetry exported ${formatDate(
                    CommitActivityData.generatedAt
                  )}.`
                : latestSignal
                ? `Repository and PR snapshot last updated ${formatDate(
                    latestSignal
                  )}. Commit telemetry refresh pending.`
                : "Showing a resilient static fallback until GitHub data is available."}
            </p>
          </div>

          <div
            className="os-radar-wrap"
            aria-label="Open source activity signal"
          >
            <div className="os-radar-orbit os-radar-orbit-one"></div>
            <div className="os-radar-orbit os-radar-orbit-two"></div>
            <div className="os-radar-orbit os-radar-orbit-three"></div>
            <span className="os-radar-node os-radar-node-one"></span>
            <span className="os-radar-node os-radar-node-two"></span>
            <span className="os-radar-node os-radar-node-three"></span>
            <div className="os-radar-core">
              <Glyph name="code" size={35} />
              <strong>{radarValue}</strong>
              <small>{radarLabel}</small>
            </div>
            <div className="os-radar-caption os-radar-caption-top">
              {hasCommitTelemetry ? "COMMIT WINDOW" : "MERGE RATE"}{" "}
              <b>{hasCommitTelemetry ? "12M" : `${mergeRate}%`}</b>
            </div>
            <div className="os-radar-caption os-radar-caption-bottom">
              {compactNumber(totalStars)} STARS / {compactNumber(totalForks)}{" "}
              FORKS
            </div>
          </div>
        </section>

        <section className="os-metrics" aria-label="Open source highlights">
          <MetricCard
            icon="code"
            label="Repositories"
            value={compactNumber(repositories.length)}
            note="public builds in the export"
          />
          <MetricCard
            icon="merge"
            label="Merged PRs"
            value={compactNumber(mergedPullRequests)}
            note={`${mergeRate}% landed successfully`}
          />
          <MetricCard
            icon="star"
            label="Community stars"
            value={compactNumber(totalStars)}
            note={`${compactNumber(totalForks)} forks across projects`}
          />
          <MetricCard
            icon="pulse"
            label="Commit telemetry"
            value={hasNumber(commitCount) ? compactNumber(commitCount) : "—"}
            note={
              hasNumber(commitCount)
                ? "verified commits in last 12 months"
                : "refresh pending — no count invented"
            }
          />
        </section>

        <section
          className="os-section os-signal-section"
          aria-labelledby="signal-heading"
        >
          <div className="os-section-heading">
            <div>
              <span className="os-section-index">01 / SIGNAL</span>
              <h2 id="signal-heading">The work, in motion.</h2>
            </div>
            <p>
              Commit history is shown only when exported from GitHub;
              pull-request change events stay separately verifiable below.
            </p>
          </div>

          <CommitTelemetry
            calendar={commitCalendar}
            totalCommits={commitCount}
            totalContributions={contributionCount}
            generatedAt={CommitActivityData && CommitActivityData.generatedAt}
          />

          <div className="os-signal-grid">
            <article className="os-velocity-panel">
              <div className="os-panel-heading">
                <div>
                  <span>PULL REQUEST VELOCITY</span>
                  <strong>Pull request cadence</strong>
                </div>
                <span className="os-panel-live">
                  <i></i> ARCHIVE
                </span>
              </div>
              <VelocityChart data={velocity} />
              <p className="os-panel-footnote">
                Active months shown from the GitHub snapshot. Hover a signal for
                its pull-request count.
              </p>
            </article>

            <article className="os-primer-panel">
              <span className="os-panel-kicker">PULL REQUEST LENS</span>
              <h3>Ship with a trail.</h3>
              <p>
                {hasCommitTelemetry
                  ? "Commit counts now come from GitHub’s contribution telemetry. Pull requests still carry their own exact line deltas, so the two signals stay useful without being blurred together."
                  : "This dashboard will not estimate a commit count from pull requests. Until telemetry is refreshed, it keeps the commit state explicit and uses exact PR change records instead."}
              </p>
              <div className="os-primer-stat">
                <Glyph name="merge" size={20} />
                <span>
                  <strong>{mergeRate}%</strong> merge success
                </span>
              </div>
              <div className="os-primer-stat">
                <Glyph name="code" size={20} />
                <span>
                  <strong>
                    {compactNumber(totalAdditions + totalDeletions)}
                  </strong>{" "}
                  changed lines recorded
                </span>
              </div>
            </article>
          </div>
        </section>

        <section
          className="os-section os-repository-section"
          id="repository-field"
          aria-labelledby="repository-heading"
        >
          <div className="os-section-heading os-repository-heading">
            <div>
              <span className="os-section-index">02 / REPOSITORY FIELD</span>
              <h2 id="repository-heading">Choose a world to enter.</h2>
            </div>
            <p>
              Filter the open-source archive by its dominant language, then tune
              the signal order.
            </p>
          </div>

          <div
            className="os-repository-controls"
            aria-label="Repository filters"
          >
            <div
              className="os-filter-list"
              role="group"
              aria-label="Filter repositories by language"
            >
              {["All", ...languages].map((language) => (
                <button
                  key={language}
                  type="button"
                  className={languageFilter === language ? "is-active" : ""}
                  onClick={() => {
                    setLanguageFilter(language);
                    setShowAllRepos(false);
                  }}
                >
                  {language}
                </button>
              ))}
            </div>
            <label className="os-sort-control">
              <span>ORDER</span>
              <select
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value)}
                aria-label="Repository order"
              >
                <option value="impact">By impact</option>
                <option value="recent">Recently updated</option>
                <option value="name">Alphabetically</option>
              </select>
            </label>
          </div>

          {displayedRepositories.length > 0 ? (
            <div className="os-repository-grid">
              {displayedRepositories.map((repo, index) => (
                <RepositoryCard
                  repo={repo}
                  index={index}
                  key={repo.id || repo.url || index}
                />
              ))}
            </div>
          ) : (
            <div className="os-empty-panel">
              <Glyph name="code" size={28} />
              <strong>No matching builds in this snapshot.</strong>
              <span>
                Try another language filter, or refresh the GitHub export to
                bring in new repositories.
              </span>
            </div>
          )}

          {visibleRepositories.length > 6 && (
            <div className="os-show-more-wrap">
              <button
                type="button"
                className="os-show-more"
                onClick={() => setShowAllRepos(!showAllRepos)}
              >
                {showAllRepos
                  ? "Show fewer repositories"
                  : `Show all ${visibleRepositories.length} repositories`}{" "}
                <Glyph name="arrow" size={16} />
              </button>
            </div>
          )}
        </section>

        <section
          className="os-section os-ledger-section"
          aria-labelledby="ledger-heading"
        >
          <div className="os-section-heading os-ledger-heading">
            <div>
              <span className="os-section-index">03 / CHANGE LEDGER</span>
              <h2 id="ledger-heading">The trail behind the build.</h2>
            </div>
            <p>
              Recent pull requests are shown as a readable engineering ledger,
              with each entry linked back to GitHub.
            </p>
          </div>

          {displayedPullRequests.length > 0 ? (
            <div className="os-pr-ledger">
              <div className="os-pr-ledger-head" aria-hidden="true">
                <span>CHANGE</span>
                <span>DATE</span>
                <span>DELTA</span>
                <span>STATUS</span>
              </div>
              {displayedPullRequests.map((pullRequest, index) => (
                <PullRequestRow
                  pullRequest={pullRequest}
                  index={index}
                  key={pullRequest.id || `${pullRequest.url}-${index}`}
                />
              ))}
            </div>
          ) : (
            <div className="os-empty-panel">
              <Glyph name="merge" size={28} />
              <strong>The ledger is waiting for its first exported PR.</strong>
              <span>
                This page stays complete without a token; a future GitHub data
                sync will populate it automatically.
              </span>
            </div>
          )}

          {sortedPullRequests.length > 6 && (
            <div className="os-show-more-wrap">
              <button
                type="button"
                className="os-show-more"
                onClick={() => setShowAllPullRequests(!showAllPullRequests)}
              >
                {showAllPullRequests
                  ? "Show recent pull requests"
                  : `Show all ${sortedPullRequests.length} pull requests`}{" "}
                <Glyph name="arrow" size={16} />
              </button>
            </div>
          )}
        </section>

        <section
          className="os-ecosystem-strip"
          aria-label="GitHub snapshot status"
        >
          <div className="os-ecosystem-icon">
            <Glyph name={trackedIssues > 0 ? "issue" : "pulse"} size={22} />
          </div>
          <div>
            <span>GITHUB SNAPSHOT STATUS</span>
            <strong>
              {trackedIssues > 0
                ? `${compactNumber(trackedIssues)} tracked issue${
                    trackedIssues === 1 ? "" : "s"
                  } in the current export.`
                : "No issue records were exported this time—repository and pull-request signals remain fully available."}
            </strong>
          </div>
          <div className="os-ecosystem-meta">
            <span>
              {organizations.length > 0
                ? `${organizations.length} contributing organizations`
                : "Personal open-source lab"}
            </span>
            <a href={githubProfile} target="_blank" rel="noopener noreferrer">
              Open GitHub profile <Glyph name="arrow" size={15} />
            </a>
          </div>
        </section>
      </main>

      <Footer theme={theme} onToggle={props.onToggle} isDark={isDark} />
      <TopButton theme={theme} />
    </div>
  );
}

export default Opensource;
